/**
 * DatePickerCompact — minimal, modern date input built on shadcn Popover + Calendar.
 *
 * Not holiday-aware (that's BusinessDatePicker). This is the lightweight one for
 * filters, reports, exports — anywhere we just need a nice date picker without
 * all the business-day logic.
 */
import { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Calendar as CalIcon, X } from "@phosphor-icons/react";

const monthsEs = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function formatPretty(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return `${d.getDate()} ${monthsEs[d.getMonth()]} ${d.getFullYear()}`;
}

export default function DatePickerCompact({
  value,                 // ISO YYYY-MM-DD or ""
  onChange,              // (iso: string) => void
  placeholder = "Fecha",
  className = "",
  disabled = false,
  minDate,               // ISO
  maxDate,               // ISO
  clearable = true,
  "data-testid": testid,
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(value + "T12:00:00") : undefined;

  const handleSelect = (d) => {
    if (!d) return;
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    onChange?.(iso);
    setOpen(false);
  };

  const disabledFn = (d) => {
    if (minDate && d < new Date(minDate + "T00:00:00")) return true;
    if (maxDate && d > new Date(maxDate + "T23:59:59")) return true;
    return false;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`inline-flex items-center justify-between gap-2 h-8 rounded border bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 px-2.5 text-xs font-mono tabular-nums transition-colors hover:border-slate-400 dark:hover:border-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-900 dark:focus:ring-white disabled:opacity-50 disabled:cursor-not-allowed ${value ? "text-slate-900 dark:text-white" : "text-slate-400 dark:text-slate-500"} ${className}`}
          data-testid={testid}
        >
          <CalIcon size={12} className="shrink-0 text-slate-400" />
          <span className="truncate">{value ? formatPretty(value) : placeholder}</span>
          {clearable && value && !disabled && (
            <span
              onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
              className="inline-flex items-center justify-center w-4 h-4 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-900 dark:hover:text-white cursor-pointer"
              role="button"
              aria-label="Limpiar fecha"
            >
              <X size={10} weight="bold" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          disabled={disabledFn}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
